import { useState, useCallback, useMemo } from 'react';
import { Application, ChangesSummaryItem } from '../types';
import {
  getCurrentDataLeads,
  getEffectiveDataLeads,
  getDataLeadFullName,
  getEnhancedDataLeads,
  normalizeLogin,
} from '../utils/dataLeadUtils';
import {
  ASSIGNMENT_MODES,
  type AssignmentMode,
} from '../constants/assignmentModes';

/**
 * Data Lead Management hook for managing data lead assignments
 * @param activeFunctionalAreaApps - Applications for the active functional area
 * @param functionalAreasData - Functional areas data
 * @param selectedFunctionalArea - Selected functional area
 */
export const useDataLeadManagement = (
  activeFunctionalAreaApps: Application[],
  functionalAreasData: any,
  selectedFunctionalArea: string,
) => {
  // Memoize the functional areas data to prevent unnecessary re-renders
  const stableFunctionalAreasData = useMemo(
    () => functionalAreasData,
    [functionalAreasData],
  );
  // State for tracking data lead assignments (applicationId -> Set of data lead logins)
  const [dataLeadAssignments, setDataLeadAssignments] = useState<
    Map<number, Set<string>>
  >(new Map());

  // State for tracking mode per application (applicationId -> AssignmentMode)
  const [applicationModes, setApplicationModes] = useState<
    Map<number, AssignmentMode>
  >(new Map());

  // State for tracking original mode per application for reset functionality
  const [originalApplicationModes, setOriginalApplicationModes] = useState<
    Map<number, AssignmentMode>
  >(new Map());

  /**
   * Helper to normalize a set of logins for case-insensitive comparison
   * @param logins - Set of login strings to normalize
   */
  const normalizeLogins = (logins: Set<string>): Set<string> => {
    return new Set(Array.from(logins).map(login => normalizeLogin(login)));
  };

  /**
   * Helper to check if two sets of logins are equal (case-insensitive)
   * @param set1 - First set of logins
   * @param set2 - Second set of logins
   */
  const areLoginsEqual = useCallback(
    (set1: Set<string>, set2: Set<string>): boolean => {
      const normalized1 = normalizeLogins(set1);
      const normalized2 = normalizeLogins(set2);

      if (normalized1.size !== normalized2.size) return false;

      for (const login of normalized1) {
        if (!normalized2.has(login)) return false;
      }

      return true;
    },
    [],
  );

  /**
   * Helper to get the expected data lead assignments for a given application and mode
   * @param app - Application to get assignments for
   * @param mode - Assignment mode (Default or Custom)
   */
  const getAssignmentsForMode = useCallback(
    (app: Application, mode: AssignmentMode): Set<string> => {
      if (mode === ASSIGNMENT_MODES.DEFAULT) {
        const functionalAreaDataLeads = getEnhancedDataLeads(
          stableFunctionalAreasData,
          selectedFunctionalArea,
        );
        return new Set(
          functionalAreaDataLeads
            .filter(lead => lead.login && lead.accountEnabled)
            .map(lead => lead.login!),
        );
      }
      const originalDataLeads = getCurrentDataLeads(app);
      return new Set(
        originalDataLeads.filter(lead => lead.login).map(lead => lead.login!),
      );
    },
    [stableFunctionalAreasData, selectedFunctionalArea],
  );

  /**
   * Initialize data lead assignments from current application responsibilities
   * @param applications - Array of applications to initialize assignments for
   */
  const initializeDataLeadAssignments = useCallback(
    (applications: Application[]) => {
      const assignments = new Map<number, Set<string>>();
      const modes = new Map<number, AssignmentMode>();

      // Get the functional area data leads for Default/Custom state logic
      const functionalAreaDataLeads = getEnhancedDataLeads(
        stableFunctionalAreasData,
        selectedFunctionalArea,
      );

      applications.forEach(app => {
        const explicitDataLeads = getCurrentDataLeads(app);
        const hasExplicitAssignments = explicitDataLeads.length > 0;

        // Determine initial mode based on whether explicit assignments exist
        const mode = hasExplicitAssignments
          ? ASSIGNMENT_MODES.CUSTOM
          : ASSIGNMENT_MODES.DEFAULT;
        modes.set(app.applicationId, mode);

        // Use effective data leads which implements Default/Custom state logic
        const effectiveDataLeads = getEffectiveDataLeads(
          app,
          functionalAreaDataLeads,
        );
        const leadLogins = new Set(effectiveDataLeads);
        assignments.set(app.applicationId, leadLogins);
      });

      setDataLeadAssignments(assignments);
      setApplicationModes(modes);
      setOriginalApplicationModes(new Map(modes)); // Store original modes for reset
    },
    [stableFunctionalAreasData, selectedFunctionalArea],
  );

  /**
   * Toggles a data lead assignment on or off for a specific application
   * @param applicationId - The ID of the application
   * @param leadLogin - The login of the lead to toggle
   * @param isAssigned - Whether the assignment should be set or unset
   */
  const toggleDataLeadAssignment = useCallback(
    (applicationId: number, leadLogin: string, isAssigned: boolean) => {
      setDataLeadAssignments(prev => {
        const newAssignments = new Map(prev);
        const currentLeads = newAssignments.get(applicationId) || new Set();
        const updatedLeads = new Set(currentLeads);

        if (isAssigned) {
          updatedLeads.add(leadLogin);
        } else {
          // Case-insensitive removal: find and remove the matching login
          const loginToRemove = Array.from(updatedLeads).find(
            existingLogin =>
              normalizeLogin(existingLogin) === normalizeLogin(leadLogin),
          );
          if (loginToRemove) {
            updatedLeads.delete(loginToRemove);
          }
        }

        newAssignments.set(applicationId, updatedLeads);

        // Update the mode based on the new assignments (synchronously)
        const functionalAreaDataLeads = getEnhancedDataLeads(
          stableFunctionalAreasData,
          selectedFunctionalArea,
        );

        const allFunctionalAreaLogins = new Set(
          functionalAreaDataLeads
            .filter(lead => lead.login && lead.accountEnabled)
            .map(lead => normalizeLogin(lead.login!)),
        );

        const currentNormalizedAssignments = new Set(
          Array.from(updatedLeads).map(login => normalizeLogin(login)),
        );

        // Determine if this should be Default mode
        // If no assignments, switch to Default mode (which will assign all functional area leads)
        const shouldBeDefaultMode =
          updatedLeads.size === 0 ||
          (allFunctionalAreaLogins.size === currentNormalizedAssignments.size &&
            Array.from(allFunctionalAreaLogins).every(login =>
              currentNormalizedAssignments.has(login),
            ));

        // If switching to Default mode due to empty assignments, populate with all functional area leads
        if (shouldBeDefaultMode && updatedLeads.size === 0) {
          functionalAreaDataLeads
            .filter(lead => lead.login && lead.accountEnabled)
            .forEach(lead => updatedLeads.add(lead.login!));
          newAssignments.set(applicationId, updatedLeads);
        }

        // Update mode immediately
        setApplicationModes(prevModes => {
          const newModes = new Map(prevModes);
          newModes.set(
            applicationId,
            shouldBeDefaultMode
              ? ASSIGNMENT_MODES.DEFAULT
              : ASSIGNMENT_MODES.CUSTOM,
          );
          return newModes;
        });

        return newAssignments;
      });
    },
    [stableFunctionalAreasData, selectedFunctionalArea],
  );

  /**
   * Reset a single row to its original state
   * @param applicationId - The ID of the application to reset
   */
  const resetRowToOriginal = useCallback(
    (applicationId: number) => {
      const app = activeFunctionalAreaApps.find(
        a => a.applicationId === applicationId,
      );
      if (!app) return;

      // Get the original mode for this application
      const originalMode =
        originalApplicationModes.get(applicationId) || ASSIGNMENT_MODES.DEFAULT;

      // Determine what the assignments should be based on original mode
      const resetAssignments = getAssignmentsForMode(app, originalMode);

      // Update the dataLeadAssignments and mode for this specific application
      setDataLeadAssignments(prev => {
        const newMap = new Map(prev);
        newMap.set(applicationId, new Set(resetAssignments));
        return newMap;
      });

      // Reset to original mode
      setApplicationModes(prevModes => {
        const newModes = new Map(prevModes);
        newModes.set(applicationId, originalMode);
        return newModes;
      });
    },
    [originalApplicationModes, activeFunctionalAreaApps, getAssignmentsForMode],
  );

  /**
   * Check if a row has changes
   * @param applicationId - The ID of the application to check for changes
   */
  const hasRowChanges = useCallback(
    (applicationId: number) => {
      const app = activeFunctionalAreaApps.find(
        a => a.applicationId === applicationId,
      );
      if (!app) return false;

      const currentAssignments =
        dataLeadAssignments.get(applicationId) || new Set();
      const currentMode =
        applicationModes.get(applicationId) || ASSIGNMENT_MODES.DEFAULT;

      // Get the original assignments based on original mode (not current mode)
      const originalMode =
        originalApplicationModes.get(applicationId) || ASSIGNMENT_MODES.DEFAULT;
      const originalAssignments = getAssignmentsForMode(app, originalMode);

      // Also check if the mode itself has changed
      const modeChanged = currentMode !== originalMode;

      // Check if sets are different or mode changed
      if (modeChanged) return true;

      return !areLoginsEqual(currentAssignments, originalAssignments);
    },
    [
      activeFunctionalAreaApps,
      originalApplicationModes,
      applicationModes,
      dataLeadAssignments,
      areLoginsEqual,
      getAssignmentsForMode,
    ],
  );

  /**
   * Get summary of changes made
   * @param selectedAppsForUpdate - Set of application IDs selected for update
   */
  const getChangesSummary = useCallback(
    (selectedAppsForUpdate: Set<number>): ChangesSummaryItem[] => {
      const changes: ChangesSummaryItem[] = [];

      // Only consider applications that are currently selected for update
      activeFunctionalAreaApps
        .filter(app => selectedAppsForUpdate.has(app.applicationId))
        .forEach(app => {
          const currentAssignments =
            dataLeadAssignments.get(app.applicationId) || new Set();
          const originalMode =
            originalApplicationModes.get(app.applicationId) ||
            ASSIGNMENT_MODES.DEFAULT;

          // Get the expected assignments based on original mode
          const expectedAssignments = getAssignmentsForMode(app, originalMode);

          const appChanges: Array<{
            action: 'added' | 'removed';
            leadLogin: string;
            leadName: string;
          }> = [];

          // Find added assignments (case-insensitive comparison)
          currentAssignments.forEach(leadLogin => {
            const isAlreadyExpected = Array.from(expectedAssignments).some(
              expectedLogin =>
                normalizeLogin(expectedLogin) === normalizeLogin(leadLogin),
            );
            if (!isAlreadyExpected) {
              const leadName = getDataLeadFullName(
                leadLogin,
                stableFunctionalAreasData,
                selectedFunctionalArea,
              );
              appChanges.push({
                action: 'added',
                leadLogin,
                leadName,
              });
            }
          });

          // Find removed assignments (case-insensitive comparison)
          expectedAssignments.forEach(leadLogin => {
            const isStillAssigned = Array.from(currentAssignments).some(
              currentLogin =>
                normalizeLogin(currentLogin) === normalizeLogin(leadLogin),
            );
            if (!isStillAssigned) {
              const leadName = getDataLeadFullName(
                leadLogin,
                stableFunctionalAreasData,
                selectedFunctionalArea,
              );
              appChanges.push({
                action: 'removed',
                leadLogin,
                leadName,
              });
            }
          });

          if (appChanges.length > 0) {
            changes.push({
              applicationId: app.applicationId,
              applicationName: `${app.name} (ID: ${app.applicationId})`,
              changes: appChanges,
            });
          }
        });

      return changes;
    },
    [
      activeFunctionalAreaApps,
      dataLeadAssignments,
      stableFunctionalAreasData,
      getAssignmentsForMode,
      originalApplicationModes,
      selectedFunctionalArea,
    ],
  );

  /**
   * Reset pending changes for specific applications after successful mutations
   * This re-initializes their state based on the fresh backend data
   * @param applicationIds - Array of application IDs to reset
   * @param applications - Fresh applications data from backend after mutations
   */
  const resetSuccessfulChanges = useCallback(
    (applicationIds: number[], applications: Application[]) => {
      // Get the functional area data leads for Default/Custom state logic
      const functionalAreaDataLeads = getEnhancedDataLeads(
        stableFunctionalAreasData,
        selectedFunctionalArea,
      );

      setDataLeadAssignments(prevAssignments => {
        const newAssignments = new Map(prevAssignments);

        applicationIds.forEach(appId => {
          const app = applications.find(a => a.applicationId === appId);
          if (!app) return;

          // Re-initialize this application's assignments from fresh backend data
          // Use effective data leads which implements Default/Custom state logic
          const effectiveDataLeads = getEffectiveDataLeads(
            app,
            functionalAreaDataLeads,
          );
          const leadLogins = new Set(effectiveDataLeads);

          newAssignments.set(appId, leadLogins);
        });

        return newAssignments;
      });

      setApplicationModes(prevModes => {
        const newModes = new Map(prevModes);

        applicationIds.forEach(appId => {
          const app = applications.find(a => a.applicationId === appId);
          if (!app) return;

          const explicitDataLeads = getCurrentDataLeads(app);
          const hasExplicitAssignments = explicitDataLeads.length > 0;
          const mode = hasExplicitAssignments
            ? ASSIGNMENT_MODES.CUSTOM
            : ASSIGNMENT_MODES.DEFAULT;

          newModes.set(appId, mode);
        });

        return newModes;
      });

      setOriginalApplicationModes(prevOriginal => {
        const newOriginal = new Map(prevOriginal);

        applicationIds.forEach(appId => {
          const app = applications.find(a => a.applicationId === appId);
          if (!app) return;

          const explicitDataLeads = getCurrentDataLeads(app);
          const hasExplicitAssignments = explicitDataLeads.length > 0;
          const mode = hasExplicitAssignments
            ? ASSIGNMENT_MODES.CUSTOM
            : ASSIGNMENT_MODES.DEFAULT;

          newOriginal.set(appId, mode);
        });

        return newOriginal;
      });
    },
    [stableFunctionalAreasData, selectedFunctionalArea],
  );

  /**
   * Clear pending changes for successful applications by marking their current state as the new "original" state.
   * This eliminates the pending changes indicator since there's no difference between current and original.
   * @param applicationIds - Array of application IDs to clear pending changes for
   */
  const clearPendingChanges = useCallback(
    (applicationIds: number[]) => {
      setOriginalApplicationModes(prevOriginal => {
        const newOriginal = new Map(prevOriginal);

        applicationIds.forEach(appId => {
          const currentMode = applicationModes.get(appId);
          if (currentMode) {
            newOriginal.set(appId, currentMode);
          }
        });
        return newOriginal;
      });
    },
    [applicationModes],
  );

  return useMemo(
    () => ({
      dataLeadAssignments,
      applicationModes,
      initializeDataLeadAssignments,
      toggleDataLeadAssignment,
      resetRowToOriginal,
      hasRowChanges,
      getChangesSummary,
      resetSuccessfulChanges,
      clearPendingChanges,
    }),
    [
      dataLeadAssignments,
      applicationModes,
      initializeDataLeadAssignments,
      toggleDataLeadAssignment,
      resetRowToOriginal,
      hasRowChanges,
      getChangesSummary,
      resetSuccessfulChanges,
      clearPendingChanges,
    ],
  );
};
